/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import React from "react";

import { extractBaseURL } from "Router";

import useProgress from "hooks/useProgress";

const LazyLoader = ({ children }: { children: React.ReactNode }) => {
  const [Progressbar] = useProgress();
  return (
    <React.Suspense
      fallback={
        <div className="fixed top-0 z-20 inset-x-0" test-id="TSGpwVGX">
          <Progressbar size="sm" test-id="L4pc5LWf" />
        </div>
      }
      test-id="c0LtotLi"
    >
      {children}
    </React.Suspense>
  );
};

export const withLazyLoader = (
  importFunc: () => Promise<any>,
  Component?: React.FC<any>,
) => {
  if (Component) {
    return (props: any) => (
      <LazyLoader test-id="hg_4u2E9">
        <Component {...props} test-id="MwFVnOFM" />
      </LazyLoader>
    );
  }
  // React.lazy takes in a single argument,
  // a function that invokes a dynamic import,
  // and returns a regular React Component.
  const LazyComponent = React.lazy(() => lazyRetry(importFunc));
  return (props: any) => (
    <LazyLoader test-id="6buXoP04">
      <LazyComponent {...props} test-id="ahzdlxMx" />
    </LazyLoader>
  );
};

export default LazyLoader;

// Expect chunk error: https://www.codemzy.com/blog/fix-chunkloaderror-react
// to solve, just refresh the page
const lazyRetry = (
  componentImport: () => Promise<any>,
  name?: string,
): Promise<{ default: React.ComponentType<any> }> => {
  if (!name) {
    name = extractBaseURL();
  }

  const sessionName = `retry-${name || "lazy"}-refreshed`;
  const retrySessionName = `retry-${name || "lazy"}-times`;
  return new Promise((resolve, reject) => {
    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem(sessionName) || "false",
    );

    const retryTimes = JSON.parse(
      window.sessionStorage.getItem(retrySessionName) || "0",
    );
    // try to import the component
    componentImport()
      .then((component) => {
        window.sessionStorage.setItem(sessionName, "false"); // success so reset the refresh
        resolve(component);
      })
      .catch((error) => {
        // limit retries to 2 to avoid infinite loop
        if (!hasRefreshed && retryTimes < 2) {
          // not been refreshed yet
          window.sessionStorage.setItem(sessionName, "true"); // we are now going to refresh
          window.sessionStorage.setItem(retrySessionName, "1");
          return window.location.reload(); // refresh the page
        }

        reject(error); // Default error behaviour as already tried refresh
      });
  });
};
